// Pull persistent conversation history from the server on agent app boot.
// Conversations live in Postgres now (Session 5.4) — nothing is auto-deleted
// and a reload should show what the agent saw before. Bus events still arrive
// in real time and append to the same store.

import type { AgentId, MessageCitation, MessageId, RichBlock, SessionId, VisitorId } from '@prakash-software/shared';
import { apiFetch } from './api';
import { withSuppressedToasts } from './event-bridge';
import { visitorDisplayName } from './visitor-name';
import {
  useConversationsStore,
  type ConversationMessage,
  type InboxPriority,
  type InboxStatus,
} from '../stores/conversations';
import { useVisitorsStore, type VisitorRow } from '../stores/visitors';

interface ServerVisitorContext {
  userAgent: string | null;
  language: string | null;
  timezone: string | null;
  viewportWidth: number | null;
  viewportHeight: number | null;
  origin: string | null;
  pagePath: string | null;
  ip: string | null;
  country: string | null;
  region: string | null;
  city: string | null;
}

interface ServerConversation {
  sessionId: string;
  siteId: string;
  visitorId: string;
  visitorName: string;
  visitorEmail: string | null;
  visitorPhone: string | null;
  visitorIsReturning: boolean;
  internal?: boolean;
  visitorContext?: ServerVisitorContext;
  agentId: string | null;
  agentName?: string | null;
  agentAvatarUrl?: string | null;
  medium: 'chat' | 'video';
  channel?: 'web' | 'api' | 'whatsapp' | 'email' | 'instagram' | 'messenger';
  startedBy: 'visitor' | 'agent' | 'ai';
  startedAt: number;
  endedAt: number | null;
  ownedBy: 'agent' | 'ai' | 'none';
  status: InboxStatus;
  priority: InboxPriority;
  tags: string[];
  departmentId?: string | null;
  departmentName?: string | null;
  starred?: boolean;
  lastMessage: {
    id: string;
    author: 'visitor' | 'agent' | 'ai' | 'system';
    text: string;
    sentAt: number;
  } | null;
}

interface ServerConversationDetail {
  session: ServerConversation & { endReason: string | null };
  messages: Array<{
    id: string;
    sessionId: string;
    sequence: number;
    author: 'visitor' | 'agent' | 'ai' | 'system';
    authorId: string | null;
    authorName: string | null;
    authorAvatarUrl: string | null;
    text: string;
    /** Server-parsed markdown; non-null for AI messages only. */
    richText?: RichBlock[] | null;
    /** Answer buttons the message offered (a flow's question). */
    quickReplies?: string[] | null;
    sentAt: number;
    deliveredAt: number | null;
    readAt: number | null;
    attachments?: Array<{ filename: string; mimeType: string; byteSize: number; url: string }>;
    citations?: MessageCitation[];
    deliveryStatus?: ConversationMessage['deliveryStatus'] | null;
    deliveryError?: string | null;
  }>;
  transcript: { summary: string; provider: string; generatedAt: number } | null;
  notes: { body: string; updatedAt: number | null; updatedBy: string | null };
  callTranscripts: Array<{
    callId: string;
    body: string;
    provider: string;
    language: string | null;
    createdAt: number;
  }>;
}

let hydrated = false;
let inFlight: Promise<void> | null = null;

/** Call after logout (or any time you want the next `hydrateFromServer()` to
 *  actually re-fetch). */
export function resetHydration(): void {
  hydrated = false;
}

export async function hydrateFromServer(force = false): Promise<void> {
  if (force) hydrated = false;
  if (hydrated) return;
  // De-dupe concurrent callers — a bus reconnect + a route remount can both
  // trigger this in the same tick.
  if (inFlight) return inFlight;
  hydrated = true;
  inFlight = doHydrate().finally(() => {
    inFlight = null;
  });
  return inFlight;
}

async function doHydrate(): Promise<void> {
  // Original body moved here so we can wrap it for de-dupe + future retry.

  // Suppress visitor/conversation toasts for the duration of the hydration —
  // none of this is "new activity" the agent needs to be notified about.
  await withSuppressedToasts(async () => {
    try {
      const { conversations } = await apiFetch<{ conversations: ServerConversation[] }>(
        '/api/conversations',
      );

      // Populate visitors store so the live visitor monitor surfaces them even
      // before any presence event arrives.
      const visitorStore = useVisitorsStore.getState();
      const seen = new Set<string>();
      for (const c of conversations) {
        if (seen.has(c.visitorId)) continue;
        seen.add(c.visitorId);
        const existing = visitorStore.rows.get(c.visitorId as VisitorId);
        if (existing) continue;
        // Build geo + context from the persisted columns the server now
        // returns. Anything absent stays null so the DeviceContext panel
        // skips that row instead of rendering "—".
        const vctx = c.visitorContext;
        const geo =
          vctx?.country || vctx?.region || vctx?.city || vctx?.ip
            ? {
                ...(vctx.country ? { country: vctx.country } : {}),
                ...(vctx.region ? { region: vctx.region } : {}),
                ...(vctx.city ? { city: vctx.city } : {}),
                ...(vctx.ip ? { ip: vctx.ip } : {}),
              }
            : undefined;
        const row: VisitorRow = {
          identity: {
            visitorId: c.visitorId as VisitorId,
            name: c.visitorName === 'Visitor' ? undefined : c.visitorName,
            email: c.visitorEmail ?? undefined,
            phone: c.visitorPhone ?? undefined,
            isReturning: c.visitorIsReturning,
            ...(c.internal ? { internal: true } : {}),
            ...(geo ? { geo } : {}),
          },
          context: {
            origin: vctx?.origin ?? '',
            pagePath: vctx?.pagePath ?? '',
            timeOnSite: 0,
            ...(vctx?.userAgent ? { userAgent: vctx.userAgent } : {}),
            ...(vctx?.language ? { language: vctx.language } : {}),
            ...(vctx?.timezone ? { timezone: vctx.timezone } : {}),
            ...(vctx?.viewportWidth && vctx?.viewportHeight
              ? { viewport: { width: vctx.viewportWidth, height: vctx.viewportHeight } }
              : {}),
          },
          // Mark as 'departed' on rehydration so the monitor renders these
          // as offline (not idle). A real presence ping flips them back if
          // they're still around. Previously we used 'idle' which the
          // monitor pill rendered literally as "IDLE" — misleading for a
          // visitor who left two days ago.
          status: 'departed',
          siteId: c.siteId,
          seenAt: c.startedAt,
          arrivedAt: c.startedAt,
          lock: null,
          unread: 0,
        };
        visitorStore.upsert(row);
      }

      const convStore = useConversationsStore.getState();
      // Recency threshold — anything older than this we treat as "history":
      // sits in byId for navigation/search but is dismissed from the rail
      // and excluded from capacity until live activity un-dismisses it.
      const STALE_AFTER_MS = 15 * 60_000; // 15 minutes
      const now = Date.now();

      for (const c of conversations) {
        // Don't clobber any conversation already populated from a live event.
        const sessId = c.sessionId as SessionId;
        const already = convStore.byId.get(sessId);
        if (already && already.messages.length > 0) continue;

        const messages: ConversationMessage[] = c.lastMessage
          ? [
              {
                id: c.lastMessage.id as MessageId,
                sessionId: sessId,
                author: c.lastMessage.author,
                text: c.lastMessage.text,
                sentAt: c.lastMessage.sentAt,
              },
            ]
          : [];

        convStore.ensure({
          sessionId: sessId,
          siteId: c.siteId,
          visitorId: c.visitorId as VisitorId,
          visitorName: visitorDisplayName(c.visitorId, c.visitorName),
          medium: c.medium,
          ...(c.channel ? { channel: c.channel } : {}),
          startedBy: c.startedBy,
          startedAt: c.startedAt,
          messages,
          notes: '',
          notesSaveState: 'idle',
          notesUpdatedAt: null,
          summary: null,
          summaryProvider: null,
          summaryGeneratedAt: null,
          callTranscripts: new Map(),
          inVideo: false,
          ownedBy: c.ownedBy === 'none' ? 'agent' : c.ownedBy,
          status: c.status,
          priority: c.priority,
          tags: c.tags,
          starred: c.starred ?? false,
          ...(c.agentId ? { ownerAgentId: c.agentId as AgentId } : {}),
          ...(c.agentName ? { ownerAgentName: c.agentName } : {}),
          ...(c.agentAvatarUrl ? { ownerAgentAvatarUrl: c.agentAvatarUrl } : {}),
        });

        // Stale conversations land in `dismissed` so they don't pollute the
        // active rail or burn a capacity slot. Live activity (visitor or AI
        // message) un-dismisses them via the conversations store's append().
        const lastActivity = c.lastMessage?.sentAt ?? c.startedAt;
        if (c.endedAt !== null || now - lastActivity > STALE_AFTER_MS) {
          convStore.dismiss(sessId);
        }
      }
    } catch (err) {
      // Non-fatal — live events will still populate the store as they arrive.
       
      console.warn('[hydration] could not hydrate from server', err);
    }
  });
}

/**
 * Catch up after a socket reconnect. Messages committed while this tab was
 * disconnected were broadcast to nobody it could hear, and hydrateFromServer
 * skips every conversation it already holds — so without this an agent's open
 * threads silently miss them until each is reopened. Re-pull every
 * conversation still on the rail; reconciliation is by message id, so nothing
 * duplicates.
 */
export async function resyncLiveConversations(): Promise<void> {
  const { byId, dismissed } = useConversationsStore.getState();
  const queue = [...byId.keys()].filter((id) => !dismissed.has(id));
  // A few at a time: a busy agent can hold dozens of threads.
  const workers = Array.from({ length: Math.min(4, queue.length) }, async () => {
    for (let id = queue.shift(); id; id = queue.shift()) await hydrateConversationDetail(id);
  });
  await Promise.all(workers);
}

/**
 * Background detail load — call when the agent opens a conversation OR switches
 * to a different one. The cached row (if any) keeps rendering while this runs,
 * then we reconcile the authoritative server payload into the store in place —
 * no blank flash, no manual refresh needed.
 *
 * `isStale` lets the caller cancel a slow response: the route passes a closure
 * that flips true on unmount / conversation switch, so a fetch that resolves
 * after the agent has moved on is dropped instead of clobbering the now-current
 * conversation (or resurrecting a closed one). It's checked both before the
 * fetch starts mutating and right before every store write.
 */
export async function hydrateConversationDetail(
  sessionId: SessionId,
  isStale?: () => boolean,
): Promise<void> {
  try {
    const data = await apiFetch<ServerConversationDetail>(
      `/api/conversations/${encodeURIComponent(sessionId)}`,
    );
    // Bail if the agent navigated away while the request was in flight — don't
    // write a now-stale conversation back into the store.
    if (isStale?.()) return;
    const convStore = useConversationsStore.getState();
    let existing = convStore.byId.get(sessionId);
    // The list-level hydrateFromServer races with the user clicking into a
    // conversation; deep-links and freshly-arrived sessions can both reach
    // this function before any conversation row exists in the store. We
    // used to silently early-return — the fetched messages got thrown
    // away and the user saw an empty thread until they reloaded. Build
    // the conversation from the detail payload instead.
    if (!existing) {
      const sess = data.session;
      convStore.ensure({
        sessionId: sess.sessionId as SessionId,
        siteId: sess.siteId,
        visitorId: sess.visitorId as VisitorId,
        visitorName: visitorDisplayName(sess.visitorId, sess.visitorName),
        medium: sess.medium,
        ...(sess.channel ? { channel: sess.channel } : {}),
        startedBy: sess.startedBy,
        startedAt: sess.startedAt,
        messages: [],
        notes: '',
        notesSaveState: 'idle',
        notesUpdatedAt: null,
        summary: null,
        summaryProvider: null,
        summaryGeneratedAt: null,
        callTranscripts: new Map(),
        inVideo: false,
        ownedBy: sess.ownedBy === 'none' ? 'agent' : sess.ownedBy,
        status: sess.status,
        priority: sess.priority,
        tags: sess.tags,
        departmentId: sess.departmentId ?? null,
        departmentName: sess.departmentName ?? null,
        starred: sess.starred ?? false,
        ...(sess.agentId ? { ownerAgentId: sess.agentId as AgentId } : {}),
        ...(sess.agentName ? { ownerAgentName: sess.agentName } : {}),
        ...(sess.agentAvatarUrl ? { ownerAgentAvatarUrl: sess.agentAvatarUrl } : {}),
      });
      existing = useConversationsStore.getState().byId.get(sessionId);
      if (!existing) return; // defensive — ensure() should have populated.
    }

    const messages: ConversationMessage[] = data.messages.map((m) => ({
      id: m.id as MessageId,
      sessionId,
      ...(m.sequence !== undefined ? { sequence: m.sequence } : {}),
      author: m.author,
      text: m.text,
      ...(m.richText?.length ? { richText: m.richText } : {}),
      ...(m.quickReplies?.length ? { quickReplies: m.quickReplies } : {}),
      sentAt: m.sentAt,
      ...(m.deliveryStatus ? { deliveryStatus: m.deliveryStatus } : {}),
      ...(m.deliveryError ? { deliveryError: m.deliveryError } : {}),
      ...(m.readAt
        ? { deliveryState: 'read' as const, deliveredAt: m.deliveredAt ?? m.readAt, readAt: m.readAt }
        : m.deliveredAt
          ? { deliveryState: 'delivered' as const, deliveredAt: m.deliveredAt }
          : {}),
      ...(m.authorId ? { authorId: m.authorId as AgentId | VisitorId } : {}),
      ...(m.authorName ? { authorName: m.authorName } : {}),
      ...(m.authorAvatarUrl ? { authorAvatarUrl: m.authorAvatarUrl } : {}),
      ...(m.attachments && m.attachments.length > 0
        ? { attachments: m.attachments }
        : {}),
      ...(m.citations?.length ? { citations: m.citations } : {}),
    }));

    // Reconcile messages by id. Server truth wins for any message the server
    // knows about (so an edited / corrected line, or a system chip the server
    // rewrote, replaces the stale cached copy instead of being skipped). Then
    // keep any purely-local messages that the server hasn't seen yet — the
    // agent's just-sent optimistic message or an in-flight bus message whose
    // round-trip to the DB hasn't completed — so a background reload never
    // drops them. Prefer the server's conversation-local sequence; optimistic
    // messages without one temporarily fall back to their client timestamp.
    const serverById = new Map(messages.map((m) => [m.id, m] as const));
    const localOnly = existing.messages.filter((m) => !serverById.has(m.id));
    const finalMessages = [...messages, ...localOnly].sort((a, b) => {
      if (a.sequence !== undefined && b.sequence !== undefined) return a.sequence - b.sequence;
      return a.sentAt - b.sentAt;
    });

    // Summary used to be injected as a system message here so reloads
    // would surface it inside the chat log. Now it has its own SummaryBlock
    // in the side rail (seeded via `hydrateConversationExtras` below), so
    // we skip the injection to avoid double-rendering on reload.

    // Re-check staleness right before the write — the agent may have switched
    // conversations between the fetch resolving and now.
    if (isStale?.()) return;
    const byId = new Map(convStore.byId);
    // Refresh authoritative session-level fields too, not just messages — a
    // background reload should also pick up an owner handoff, a (re)named
    // visitor, or a star toggled from another tab/agent. Local-only UI state
    // (notes draft, video flags, etc.) is preserved by spreading `existing`.
    byId.set(sessionId, {
      ...existing,
      visitorName: visitorDisplayName(data.session.visitorId, data.session.visitorName),
      // 'none' = agent-first, waiting for a human → treat as agent-handled so
      // the UI offers "Hand off" (to AI), not "Take over" (from AI).
      ownedBy: data.session.ownedBy === 'none' ? 'agent' : data.session.ownedBy,
      status: data.session.status,
      priority: data.session.priority,
      tags: data.session.tags,
      departmentId: data.session.departmentId ?? null,
      departmentName: data.session.departmentName ?? null,
      ...(data.session.agentId ? { ownerAgentId: data.session.agentId as AgentId } : {}),
      ...(data.session.agentName ? { ownerAgentName: data.session.agentName } : {}),
      ...(data.session.agentAvatarUrl
        ? { ownerAgentAvatarUrl: data.session.agentAvatarUrl }
        : {}),
      starred: data.session.starred ?? existing.starred ?? false,
      messages: finalMessages,
    });
    useConversationsStore.setState({ byId });

    // Seed notes, summary, and per-call audio transcripts from the same
    // detail payload — one round-trip covers everything the Conversation
    // page needs to render the side rail.
    convStore.hydrateConversationExtras(sessionId, {
      siteId: data.session.siteId,
      notes: { body: data.notes.body, updatedAt: data.notes.updatedAt },
      summary: data.transcript,
      callTranscripts: data.callTranscripts.map((t) => ({
        callId: t.callId,
        body: t.body,
        provider: t.provider,
        language: t.language,
        createdAt: t.createdAt,
      })),
    });
  } catch (err) {
     
    console.warn('[hydration] conversation detail fetch failed', err);
  }
}
